import { httpGet, httpPost, removeTrailingTags } from '../../utils';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { NODE_TYPE } from '@silverfort-nodelibs/common/utils/consts';
import { SoftwareStatus } from './types';

const REFRESH_INTERVAL = 3000;
const CHECK_FOR_UPDATES_INTERVAL = 60000;

export const DEFAULT_STATUS: SoftwareStatus = {
    autoADAdapterUpgrade: true,
    allowAzureOpenAiUsage: false,
    currentTowerVersion: null,
    currentCastleVersion: null,
    maxTowerVersion: null,
    lastDownloadedVersion: null,
    downloading: false,
    downloadingProgress: 0,
    downloadError: '',
    upgradingTower: false,
    towerUpgradeProgress: 0,
    towerUpgradeDescription: '',
    towerUpgradeError: '',
    upgradingNodes: false,
    nodesUpgradeProgress: 0,
    nodesUpgradeDescription: '',
    nodesUpgradeError: '',
    upgradingPushServices: false,
    pushServicesUpgradeProgress: 0,
    pushServicesUpgradeDescription: '',
    pushServicesUpgradeError: '',
    upgradingDcs: false,
    dcsUpgradeProgress: 0,
    dcsUpgradeDescription: '',
    dcsUpgradeError: '',
    companyLogo: 'company_logo.png',
    showLogs: false,
    logsData: [],
    showUnprotected: false,
    failedComponents: [],
    inProgressComponents: [],
    queuedComponents: [],
    lastCheck: Date.now()
};

export const checkForUpdatesHandler = async ({ setSoftwareStatus, statusInterval, setNodesData }) => {
    try {
        const res = await httpGet('/softwareUpdates/check');
        if (res && res.data) {
            const data = res.data as unknown as SoftwareStatus;
            setSoftwareStatus((prevStatus) => ({
                ...prevStatus,
                ...data,
                currentTowerVersion: removeTrailingTags(data.currentTowerVersion),
                lastDownloadedVersion: removeTrailingTags(data.lastDownloadedVersion),
                maxTowerVersion: removeTrailingTags(data.maxTowerVersion),
                versionUpgradeInProgress: data.versionUpgradeInProgress
                    ? removeTrailingTags(data.versionUpgradeInProgress)
                    : null
            }));
        }
    } catch (error) {
        console.error(error);
    } finally {
        if (!statusInterval.current) {
            statusInterval.current = setInterval(() => checkStatus(setSoftwareStatus, setNodesData), REFRESH_INTERVAL);
        }
        setTimeout(() => {
            statusInterval?.current &&
                checkForUpdatesHandler({
                    setSoftwareStatus,
                    statusInterval,
                    setNodesData
                });
        }, CHECK_FOR_UPDATES_INTERVAL);
    }
};

export const checkStatus = async (setSoftwareStatus, setNodesData) => {
    try {
        const res = await httpGet('/softwareUpdates/status');
        if (res && res.data) {
            const data = res.data as unknown as SoftwareStatus;
            setSoftwareStatus((prevStatus) => ({
                ...prevStatus,
                ...data,
                currentTowerVersion: removeTrailingTags(data.currentTowerVersion),
                lastDownloadedVersion: removeTrailingTags(data.lastDownloadedVersion),
                maxTowerVersion: removeTrailingTags(data.maxTowerVersion),
                versionUpgradeInProgress: data.versionUpgradeInProgress
                    ? removeTrailingTags(data.versionUpgradeInProgress)
                    : null
            }));
        }
    } catch (error) {
        console.error(error);
    }

    const res = await httpGet('/softwareUpdates/nodeVersions');
    if (res && res.data) {
        updateNodesStateFromRes(res.data, setNodesData, setSoftwareStatus);
    }
};

export const checkConnectivityHandler = async ({ systemMode, setNodesData, setSoftwareStatus }) => {
    try {
        let nodesRes;
        if (systemMode && systemMode === SYSTEM_MODES.ON_PREM) {
            nodesRes = await httpGet('/softwareUpdates/checkNodesConnectivity');
        } else {
            nodesRes = await httpGet('/softwareUpdates/nodeVersions');
        }
        updateNodesStateFromRes(nodesRes.data, setNodesData, setSoftwareStatus);
    } catch (err) {
        console.error(err);
    } finally {
        setTimeout(() => {
            checkConnectivityHandler({
                systemMode,
                setNodesData,
                setSoftwareStatus
            });
        }, CHECK_FOR_UPDATES_INTERVAL);
    }
};

export const updateNodesStateFromRes = (resData, setNodesData, setSoftwareStatus) => {
    const nodes = resData.nodes.filter((n) => n.type === NODE_TYPE.WALL);
    const adConnects = resData.nodes.filter((n) => n.type === NODE_TYPE.AD_CONNECT);
    const pushServices = resData.nodes.filter((n) => n.type === NODE_TYPE.ARYA);
    const currentTowerPlatform = resData.currentTowerPlatform;

    const dcs = resData.nodes.filter((n) => n.type === NODE_TYPE.DC);

    setNodesData({ nodes, dcs, pushServices, adConnects });
    setSoftwareStatus((prevStatus) => ({
        ...prevStatus,
        timeToForceDcsUpgrade: resData.timeToForceDcsUpgrade,
        currentTowerPlatform: currentTowerPlatform
    }));
};

export const NODES_TYPES = {
    NODES: 'nodes',
    DCS: 'dcs',
    PUSH_SERVICES: 'pushServices'
};

export const calcTabs = (nodes, dcs, pushServices) => {
    const tabs: { name: string; value: string }[] = [];
    if (pushServices?.length > 0) {
        tabs.push({ name: `Desktop Messaging Services (${pushServices.length})`, value: NODES_TYPES.PUSH_SERVICES });
    }
    tabs.push({ name: `Nodes (${nodes.length})`, value: NODES_TYPES.NODES });
    tabs.push({ name: `DCs (${dcs.length})`, value: NODES_TYPES.DCS });
    return tabs;
};

export const handleAutoDcUpgrade = async (checked: boolean, setSoftwareStatus) => {
    try {
        await httpPost('/softwareUpdates/setAutoADAdapterUpgrade', { autoADAdapterUpgrade: checked });
        setSoftwareStatus((prevStatus) => ({
            ...prevStatus,
            autoADAdapterUpgrade: checked
        }));
    } catch (error) {
        console.error(error);
    }
};
